//* Libraries imports
import { motion } from 'framer-motion';

//? Styles imports
import './resource-question-skeleton.scss';

interface ResourceQuestionSkeletonProps {
  /**
   * Shows skeleton for process status instead of form
   */
  showProcessStatus?: boolean;
  /**
   * Shows skeleton for edit mode (simplified form)
   */
  isEditMode?: boolean;
}

export function ResourceQuestionSkeleton({ 
  showProcessStatus = false, 
  isEditMode = false 
}: ResourceQuestionSkeletonProps) {
  const skeletonVariants = {
    pulse: {
      opacity: [0.6, 1, 0.6],
      transition: {
        duration: 1.5,
        repeat: Infinity
      }
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        duration: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  // Process Status Skeleton (when process is not finished)
  if (showProcessStatus) {
    return (
      <motion.div
        className="resource-question-skeleton"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="d-flex justify-content-center align-items-center min-vh-100 w-100">
          <motion.div 
            className="skeleton-process-status"
            variants={itemVariants}
          >
            {/* Status Icon */}
            <motion.div
              className="skeleton-status-icon mb-4"
              variants={skeletonVariants}
              animate="pulse"
            />
            
            {/* Status Title */}
            <motion.div
              className="skeleton-status-title mb-2"
              variants={skeletonVariants}
              animate="pulse"
            />
            
            {/* Status Subtitle */}
            <motion.div
              className="skeleton-status-subtitle mb-3"
              variants={skeletonVariants}
              animate="pulse"
            />
            
            {/* Status Message */}
            <motion.div
              className="skeleton-status-message mb-4"
              variants={skeletonVariants}
              animate="pulse"
            />
            
            {/* Action Button */}
            <motion.div
              className="skeleton-status-button"
              variants={skeletonVariants}
              animate="pulse"
            />
          </motion.div>
        </div>
      </motion.div>
    );
  }

  // Main Form Skeleton
  return (
    <motion.div
      className="resource-question-skeleton"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <div className="resource-question-form-container">
        {/* Card Skeleton */}
        <div className="card shadow-sm">
          {/* Card Header Skeleton */}
          <motion.div 
            className="card-header bg-white"
            variants={itemVariants}
          >
            <motion.div
              className="skeleton-card-title mb-0"
              variants={skeletonVariants}
              animate="pulse"
            />
          </motion.div>

          {/* Card Body Skeleton */}
          <div className="card-body">
            {/* Error Message Skeleton (hidden by default) */}
            <motion.div 
              className="skeleton-alert mb-4 d-none"
              variants={itemVariants}
            >
              <motion.div
                className="skeleton-alert-content"
                variants={skeletonVariants}
                animate="pulse"
              />
            </motion.div>

            {/* Header Info Section - 2 columns layout */}
            <motion.div 
              className="row mb-4"
              variants={itemVariants}
            >
              <div className="col-md-6">
                <div className="mb-3">
                  <motion.div
                    className="skeleton-label mb-2"
                    variants={skeletonVariants}
                    animate="pulse"
                  />
                  <motion.div
                    className="skeleton-input-field"
                    variants={skeletonVariants}
                    animate="pulse"
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <motion.div
                    className="skeleton-label mb-2"
                    variants={skeletonVariants}
                    animate="pulse"
                  />
                  <motion.div
                    className="skeleton-input-field"
                    variants={skeletonVariants}
                    animate="pulse"
                  />
                </div>
              </div>
            </motion.div>

            {/* Candidate Info Section */}
            <motion.div 
              className="mb-4"
              variants={itemVariants}
            >
              <div className="mb-3">
                <motion.div
                  className="skeleton-label mb-2"
                  variants={skeletonVariants}
                  animate="pulse"
                />
                <motion.div
                  className="skeleton-input-readonly"
                  variants={skeletonVariants}
                  animate="pulse"
                />
              </div>
            </motion.div>

            {/* Deadline Info Section */}
            <motion.div 
              className="deadline-info mb-4"
              variants={itemVariants}
            >
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <motion.div
                      className="skeleton-label mb-2"
                      variants={skeletonVariants}
                      animate="pulse"
                    />
                    <motion.div
                      className="skeleton-input-field"
                      variants={skeletonVariants}
                      animate="pulse"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <motion.div
                      className="skeleton-label mb-2"
                      variants={skeletonVariants}
                      animate="pulse"
                    />
                    <motion.div
                      className="skeleton-input-field"
                      variants={skeletonVariants}
                      animate="pulse"
                    />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Exam Selection Section */}
            {!isEditMode && (
              <>
                {/* Test Selection */}
                <motion.div 
                  className="mb-4"
                  variants={itemVariants}
                >
                  <div className="mb-3">
                    <motion.div
                      className="skeleton-label mb-2"
                      variants={skeletonVariants}
                      animate="pulse"
                    />
                    <motion.div
                      className="skeleton-select-field"
                      variants={skeletonVariants}
                      animate="pulse"
                    />
                  </div>
                </motion.div>

                {/* Discipline Selection */}
                <motion.div 
                  className="mb-4"
                  variants={itemVariants}
                >
                  <div className="mb-3">
                    <motion.div
                      className="skeleton-label mb-2"
                      variants={skeletonVariants}
                      animate="pulse"
                    />
                    <motion.div
                      className="skeleton-select-field"
                      variants={skeletonVariants}
                      animate="pulse"
                    />
                  </div>
                </motion.div>

                {/* Question Selection */}
                <motion.div 
                  className="mb-4"
                  variants={itemVariants}
                >
                  <div className="mb-3">
                    <motion.div
                      className="skeleton-label mb-2"
                      variants={skeletonVariants}
                      animate="pulse"
                    />
                    <motion.div
                      className="skeleton-select-field"
                      variants={skeletonVariants}
                      animate="pulse"
                    />
                  </div>
                </motion.div>
              </>
            )}

            {/* Description Fields Section */}
            <motion.div 
              className="mb-4"
              variants={itemVariants}
            >
              {/* Description Field */}
              <div className="mb-4">
                <motion.div
                  className="skeleton-label mb-2"
                  variants={skeletonVariants}
                  animate="pulse"
                />
                <motion.div
                  className="skeleton-textarea-field"
                  variants={skeletonVariants}
                  animate="pulse"
                />
              </div>

              {/* Fundamentacao Field */}
              <div className="mb-4">
                <motion.div
                  className="skeleton-label mb-2"
                  variants={skeletonVariants}
                  animate="pulse"
                />
                <motion.div
                  className="skeleton-textarea-field"
                  variants={skeletonVariants}
                  animate="pulse"
                />
              </div>

              {/* Pedido Field */}
              <div className="mb-4">
                <motion.div
                  className="skeleton-label mb-2"
                  variants={skeletonVariants}
                  animate="pulse"
                />
                <motion.div
                  className="skeleton-input-field"
                  variants={skeletonVariants}
                  animate="pulse"
                />
              </div>
            </motion.div>

            {/* File Upload Section */}
            <motion.div 
              className="mb-4"
              variants={itemVariants}
            >
              <motion.div
                className="skeleton-label mb-2"
                variants={skeletonVariants}
                animate="pulse"
              />
              <div className="file-upload-container">
                <motion.div
                  className="skeleton-file-upload-area mb-3"
                  variants={skeletonVariants}
                  animate="pulse"
                />
                
                {/* File List Skeleton */}
                <div className="skeleton-file-list">
                  {[1, 2].map((index) => (
                    <motion.div
                      key={index}
                      className="skeleton-file-item mb-2"
                      variants={skeletonVariants}
                      animate="pulse"
                    />
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Action Buttons */}
            <motion.div 
              className="d-flex justify-content-end"
              variants={itemVariants}
            >
              <div className="me-2">
                <motion.div
                  className="skeleton-button skeleton-button-secondary"
                  variants={skeletonVariants}
                  animate="pulse"
                />
              </div>
              <motion.div
                className="skeleton-button skeleton-button-primary"
                variants={skeletonVariants}
                animate="pulse"
              />
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
